/**
 * 放送地点一覧の JSON Import/Export ボタン群。
 *
 * - Export: 親から渡された一覧をそのまま JSON ファイル化してダウンロードする。
 * - Import: ファイルを選択 → パース → 確認ダイアログ → 親から渡された一括 upsert 関数
 *   を順次実行する。新規 / 更新 / 失敗の件数を結果としてコールバックする。
 *
 * Import は「spotId が Import 先に存在すれば更新、存在しなければ新規作成」という
 * upsert 挙動である (判定と API 呼び出しは親の `onImport` が担う)。
 * 全件 Export → 別環境 (0 件 or 数件) へ Import する運用を想定しており、
 * 件数の上限は無い (1 件 = 1 リクエストの直列実行)。件数が多いと時間がかかるため、
 * 実行中は `(処理済み/全件)` の進捗を表示する。
 */
import { Spinner } from './Spinner';
import { useRef, useState } from 'react';

import type { BroadcastPoint } from '../api/types';
import { exportBroadcastPointsAsJson, parseImportJson } from '../utils/jsonImportExport';

import { ConfirmDialog } from './ConfirmDialog';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';

/** Import の実行結果 (件数内訳 + 失敗詳細) */
export interface ImportResult {
  /** 新規作成できた件数 */
  created: number;
  /** 既存地点を更新できた件数 */
  updated: number;
  /** 失敗した項目の説明 (画面にそのまま表示する) */
  failures: string[];
}

/** 確認ダイアログに出す事前サマリ (実行前の見積り) */
export interface ImportSummary {
  /** 既存地点の更新になる見込みの件数 */
  updateCount: number;
  /** 新規作成になる見込みの件数 */
  createCount: number;
}

/** Import 進捗の通知 (処理済み件数 / 全件数) */
export type ImportProgressHandler = (done: number, total: number) => void;

interface ImportExportControlProps {
  /** Export 対象の現在の一覧 */
  items: BroadcastPoint[];
  /**
   * Import 用の一括 upsert 関数 (実装は親側で API を呼ぶ)。
   *
   * spotId が Import 先に存在すれば PUT、存在しなければ POST で新規作成する。
   * 1 件処理するごとに `onProgress(done, total)` を呼び出して進捗を通知する。
   */
  onImport: (items: BroadcastPoint[], onProgress: ImportProgressHandler) => Promise<ImportResult>;
  /**
   * 確認ダイアログに「更新 M 件 / 新規 N 件」を出すための事前サマリ関数 (任意)。
   *
   * 未指定の場合は件数のみを表示する。判定は現在読み込み済みの一覧との照合なので
   * 厳密ではない (別方面の地点は一覧に無く「新規」に見える)。実行時は 404/409 の
   * フォールバックで正しい側へ振り直されるため、あくまで目安として表示する。
   */
  summarizeImport?: (items: BroadcastPoint[]) => ImportSummary;
}

export function ImportExportControl(props: ImportExportControlProps): React.JSX.Element {
  const { items, onImport, summarizeImport } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /**
   * Import 実行中の進捗 (処理済み件数 / 全件数)。
   * 実行前・完了後は null。数百件の Import でも「止まって見えない」ようにするため。
   */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /**
   * Import 確認ダイアログの対象。
   * パース成功 → ダイアログ表示の流れで一時保持する。null のときダイアログ非表示。
   */
  const [pendingImport, setPendingImport] = useState<BroadcastPoint[] | null>(null);

  const handleExport = (): void => {
    exportBroadcastPointsAsJson(items);
  };

  const handleImportClick = (): void => {
    setResultMessage(null);
    setErrorMessage(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    let parsed: BroadcastPoint[];
    try {
      const text = await file.text();
      parsed = parseImportJson(text);
    } catch (err) {
      setErrorMessage(describeApiErrorMessage(err));
      return;
    }

    if (parsed.length === 0) {
      setErrorMessage('JSON に放送地点が含まれていません');
      return;
    }

    // 確認ダイアログを表示するため一時保持。実 Import は executeImport で行う。
    setPendingImport(parsed);
  };

  const executeImport = async (): Promise<void> => {
    const parsed = pendingImport;
    setPendingImport(null);
    if (!parsed) return;

    setImporting(true);
    setProgress({ done: 0, total: parsed.length });
    try {
      const result = await onImport(parsed, (done, total) => {
        setProgress({ done, total });
      });
      const failureCount = result.failures.length;
      const breakdown = `更新: ${result.updated} 件 / 新規: ${result.created} 件`;
      if (failureCount === 0) {
        setResultMessage(`Import が完了しました (${breakdown})`);
      } else {
        setResultMessage(
          `Import 完了 (${breakdown} / 失敗: ${failureCount} 件)\n失敗詳細: ${result.failures.join(' / ')}`,
        );
      }
    } catch (err) {
      setErrorMessage(describeApiErrorMessage(err));
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  /** 確認ダイアログの本文 (件数 + 更新/新規の内訳) */
  const confirmMessage = ((): string => {
    if (!pendingImport) return '';
    const base = `${pendingImport.length} 件の放送地点を Import します。`;
    if (!summarizeImport) return `${base}よろしいですか？`;
    const { updateCount, createCount } = summarizeImport(pendingImport);
    return `${base}\n(更新: ${updateCount} 件 / 新規: ${createCount} 件)\nよろしいですか？`;
  })();

  return (
    <>
      <button
        type="button"
        data-testid="export-button"
        onClick={handleExport}
        disabled={items.length === 0}
      >
        JSON Export
      </button>
      <button
        type="button"
        data-testid="import-button"
        onClick={handleImportClick}
        disabled={importing}
      >
        {importing ? (
          <>
            <Spinner />
            {progress ? `Import 中... (${progress.done}/${progress.total})` : 'Import 中...'}
          </>
        ) : (
          'JSON Import'
        )}
      </button>
      <input
        type="file"
        data-testid="import-file-input"
        accept="application/json"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      {progress ? (
        <div data-testid="import-progress" style={{ flexBasis: '100%' }}>
          <Spinner />
          Import 中... ({progress.done}/{progress.total} 件)
          <progress value={progress.done} max={progress.total} style={{ marginLeft: 8 }} />
        </div>
      ) : null}
      {errorMessage ? (
        <div className="error-message" style={{ flexBasis: '100%' }}>
          {errorMessage}
        </div>
      ) : null}
      {resultMessage ? (
        <div className="success-message" style={{ flexBasis: '100%', whiteSpace: 'pre-wrap' }}>
          {resultMessage}
        </div>
      ) : null}

      {/* Import 確認ダイアログ (window.confirm の代替) */}
      <ConfirmDialog
        open={pendingImport !== null}
        title="JSON Import の実行確認"
        message={confirmMessage}
        onConfirm={() => void executeImport()}
        onCancel={() => setPendingImport(null)}
      />
    </>
  );
}
